import type React from 'react'
import { useEffect, useRef, useState } from 'react'

export const useFadeInOnScroll = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          if (ref.current) {
            observer.unobserve(ref.current)
          }
        }
      },
      { threshold }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [threshold])

  return { ref, isVisible }
}

export const fadeInStyle = (isVisible: boolean, delay = 0) => ({
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
  transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
})

export const hoverLiftStyle = {
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
  },
}

interface HoverOptions {
  lift?: number
  shadow?: string
  hoverBorderColor?: string
  defaultBorderColor?: string
}

export const createHoverHandlers = ({
  lift = 4,
  shadow = '0 12px 32px rgba(0, 0, 0, 0.08)',
  hoverBorderColor,
  defaultBorderColor,
}: HoverOptions = {}) => ({
  onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
    const el = event.currentTarget
    el.style.transform = `translateY(-${lift}px)`
    el.style.boxShadow = shadow
    if (hoverBorderColor) {
      el.style.borderColor = hoverBorderColor
    }
  },
  onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
    const el = event.currentTarget
    el.style.transform = 'translateY(0)'
    el.style.boxShadow = 'none'
    if (hoverBorderColor && defaultBorderColor) {
      el.style.borderColor = defaultBorderColor
    }
  },
})
